import { useState, useLayoutEffect } from "react";
import { useLocalization } from "@fluent/react";
import { Combobox, Row, TextInput } from "@narmi/design_system";
import { SUPPORTED_COUNTRY_CODES } from "byzantine/src/dbbl/businessLogic/entities/recipients/forms/SwiftDestination.form";
import { getCountryCodeFromBic } from "byzantine/src/dbbl/businessLogic/modules/internationalWires/utils";
import {
  CountryCode,
  getCallingCodeForCountry,
  parseInternationalPhoneNumber,
} from "byzantine/src/dbbl/utils/internationalPhoneNumber";
import styles from "./RecipientDrawer.module.scss";

const getDefaultCallingCodeFromBic = (bic: string) => {
  const countryCode = getCountryCodeFromBic(bic);
  const callingCode = getCallingCodeForCountry(countryCode as CountryCode);
  return callingCode ? `+${callingCode}` : "";
};

interface CountryCallingCodeSelectorProps {
  value: string;
  label: string;
  onChange: (countryCallingCode: string) => void;
  error?: string;
}

const CountryCallingCodeSelector = ({
  value,
  label,
  onChange,
  error,
}: CountryCallingCodeSelectorProps) => {
  const selectedCountry = SUPPORTED_COUNTRY_CODES.find(
    (c) => c.countryCallingCode === value
  );
  const [inputValue, setInputValue] = useState<string>(
    selectedCountry ? selectedCountry.label : ""
  );

  // whenever `value` changes externally, update inputValue (what is displayed in the input) to match
  useLayoutEffect(() => {
    setInputValue(selectedCountry?.label ?? "");
  }, [value, selectedCountry]);

  const handleChange = (c: string) => {
    onChange(c);
    const pickedCountry = SUPPORTED_COUNTRY_CODES.find(
      (r) => r.countryCallingCode === c
    );
    setInputValue(pickedCountry?.label ?? "");
  };

  return (
    <Combobox
      label={label}
      onChange={handleChange}
      errorText={error}
      inputValue={inputValue}
      onInputChange={(updatedValue: string) => setInputValue(updatedValue)}
      filterItemsByInput={(items: any[], typedValue: string) =>
        items.filter((item) => {
          const query = item.props.searchValue || item.props.value;
          return query.toLowerCase().includes(typedValue.toLowerCase());
        })
      }
    >
      {SUPPORTED_COUNTRY_CODES.map((c) => (
        <Combobox.Item
          key={c.countryCallingCode}
          value={c.countryCallingCode}
          searchValue={c.label}
        >
          <Row gapSize="xxs">
            <Row.Item>{c.label}</Row.Item>
          </Row>
        </Combobox.Item>
      ))}
    </Combobox>
  );
};

interface InternationalPhoneInputProps {
  onChange: (value: string) => void;
  value: string;
  error?: any;
  bic?: string;
}

const InternationalPhoneInput = ({
  onChange,
  value,
  error,
  bic = "",
}: InternationalPhoneInputProps) => {
  const { l10n } = useLocalization();
  const {
    countryCallingCode: countryCallingCodeValue,
    nationalNumber: nationalNumberValue,
  } = parseInternationalPhoneNumber(value);
  const [countryCallingCode, setCountryCallingCode] = useState(
    countryCallingCodeValue || getDefaultCallingCodeFromBic(bic)
  );
  const [nationalNumber, setNationalNumber] = useState(nationalNumberValue);

  const handleValueChange = (
    newCountryCode: string,
    newNationalNumber: string
  ) => {
    const newValue = `${newCountryCode || ""}${newNationalNumber || ""}`;
    onChange(newValue);
  };

  const onChangeCountryCode = (newCountryCode: string) => {
    setCountryCallingCode(newCountryCode);
    handleValueChange(newCountryCode, nationalNumber);
  };
  const onChangeNationalNumber = (newNationalNumber: string) => {
    setNationalNumber(newNationalNumber);
    handleValueChange(countryCallingCode, newNationalNumber);
  };
  return (
    <div className={`${styles.phoneInput}`} key="phone">
      <div className={styles.countryCode}>
        <CountryCallingCodeSelector
          label={l10n.getString("label-international-wire-country-code")}
          onChange={onChangeCountryCode}
          value={countryCallingCode}
        />
      </div>
      <div className={`margin--left--s ${styles.nationalNumber}`}>
        <TextInput
          label={l10n.getString("label-international-wire-phone-number")}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onChangeNationalNumber(e.target.value)
          }
          value={nationalNumber}
          error={error}
        />
      </div>
    </div>
  );
};

export default InternationalPhoneInput;
